import React, { useState } from 'react';
import { Box, CircularProgress } from '@mui/material';
import {
  AlertTriangle,
  ArrowDownRight,
  ArrowUpRight,
  Brain,
  Building2,
  CheckCircle2,
  ChevronDown,
  History,
  Layers,
  Lightbulb,
  Sparkles,
  Users,
  Bot,
} from 'lucide-react';
import type { CategoryComparisonItem, PracticeComparisonResponse } from '@/api/meetingService';
import { useMeetingPracticeComparison } from '@/hooks/UseMeeting';
import { useDashboardTheme } from './themeHelper';

interface PracticeGrowthViewProps {
  meetingId: string;
  realData?: Record<string, unknown>;
}

interface RealDataShape {
  company?: string;
  customerData?: { companyName?: string };
  TargetCompanyProfile?: { companyName?: string };
  meetingNotes?: { matchedCompanyName?: string; matchedContactName?: string };
  companyContact?: { firstName?: string; lastName?: string };
  customerContact?: { firstName?: string; lastName?: string; fullName?: string };
  practiceComparison?: PracticeComparisonResponse;
  ai_summary?: { practiceComparison?: PracticeComparisonResponse };
}

/**
 * Expandable bullet list matching Pitchster Analysis design
 */
const ExpandableBulletList: React.FC<{
  items: string[];
  previewCount?: number;
  isDarkMode: boolean;
}> = ({ items, previewCount = 3, isDarkMode }) => {
  const [expanded, setExpanded] = useState(false);

  if (!items?.length) return null;

  const remaining = items.length - previewCount;
  const hasMore = remaining > 0;
  const visibleItems = expanded ? items : items.slice(0, previewCount);

  return (
    <div className="space-y-2">
      <ul className="space-y-2">
        {visibleItems.map((item, i) => (
          <li
            key={i}
            className={`text-md list-disc list-inside leading-relaxed ${
              isDarkMode ? 'text-white/80' : 'text-slate-700 font-medium'
            }`}
          >
            {item}
          </li>
        ))}
      </ul>

      {hasMore && (
        <button
          onClick={() => setExpanded((prev) => !prev)}
          className={`flex items-center gap-1 text-sm font-bold cursor-pointer transition-colors mt-2 ${
            isDarkMode
              ? 'text-[#B8F890] hover:text-[#B8F890]/80'
              : 'text-emerald-700 hover:text-emerald-900'
          }`}
          style={{ background: 'transparent', border: 'none', padding: 0 }}
        >
          {expanded ? 'View less' : `View more (${remaining})`}
          <ChevronDown
            className={`w-4 h-4 transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`}
          />
        </button>
      )}
    </div>
  );
};

/**
 * Semi-circle gauge matching Deal Intelligence / Performance metrics
 */
const SemiCircleGauge: React.FC<{
  value: number;
  trackColor: string;
  fillColor: string;
  label: string;
  labelColor: string;
  isDarkMode: boolean;
  size?: 'md' | 'lg';
}> = ({ value, trackColor, fillColor, label, labelColor, isDarkMode, size = 'md' }) => {
  const radius = 40;
  const circumference = Math.PI * radius;
  const clamped = Math.min(Math.max(value, 0), 100);
  const offset = circumference * (1 - clamped / 100);
  const maxWidth = size === 'lg' ? '180px' : '130px';
  const fontSize = size === 'lg' ? 22 : 18;
  const labelSize = size === 'lg' ? 'text-sm' : 'text-[11px]';

  return (
    <div className="flex flex-col items-center flex-1 min-w-0">
      <svg viewBox="0 0 100 56" className="w-full" style={{ maxWidth }}>
        <path
          d="M10,50 A40,40 0 0,1 90,50"
          fill="none"
          stroke={trackColor}
          strokeWidth="9"
          strokeLinecap="round"
        />
        <path
          d="M10,50 A40,40 0 0,1 90,50"
          fill="none"
          stroke={fillColor}
          strokeWidth="9"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          style={{ transition: 'stroke-dashoffset 0.6s ease' }}
        />
        <text
          x="50"
          y="46"
          textAnchor="middle"
          fontSize={fontSize}
          fontWeight="800"
          fill={isDarkMode ? '#ffffff' : '#0f172a'}
        >
          {clamped}%
        </text>
      </svg>
      <span className={`${labelSize} font-bold mt-1 text-center`} style={{ color: labelColor }}>
        {label}
      </span>
    </div>
  );
};

export const PracticeGrowthView: React.FC<PracticeGrowthViewProps> = ({ meetingId, realData }) => {
  const { isDark, colors } = useDashboardTheme();
  const isDarkMode = isDark;

  const typedRealData = realData as RealDataShape | undefined;
  const initialComparison =
    typedRealData?.practiceComparison || typedRealData?.ai_summary?.practiceComparison || undefined;

  const {
    data: comparison,
    isLoading,
    isError,
    refetch,
  } = useMeetingPracticeComparison(meetingId, initialComparison);

  const [expandedRoadmap, setExpandedRoadmap] = useState<Set<number>>(new Set([0]));

  const toggleRoadmap = (index: number) => {
    setExpandedRoadmap((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  if (isLoading) {
    return (
      <Box className="flex flex-col items-center justify-center min-h-[350px] space-y-4">
        <CircularProgress size={40} sx={{ color: isDarkMode ? '#B8F890' : '#16A34A' }} />
        <p className={`text-sm font-medium ${isDarkMode ? 'text-white/60' : 'text-slate-500'}`}>
          Aggregating practice pitch baseline and generating AI comparison...
        </p>
      </Box>
    );
  }

  if (isError || !comparison) {
    return (
      <Box
        className="p-6 rounded-[16px] text-center my-6"
        style={{
          background: colors.surface,
          border: `1px solid ${colors.border}`,
          color: colors.text,
        }}
      >
        <AlertTriangle className="w-9 h-9 text-amber-400 mx-auto mb-3" />
        <h3 className="text-lg font-bold">Could not load Practice Comparison</h3>
        <p className={`text-sm mt-1 mb-4 ${isDarkMode ? 'text-white/60' : 'text-slate-500'}`}>
          Failed to fetch practice vs real meeting comparison data.
        </p>
        <button
          onClick={() => refetch()}
          className="px-5 py-2 rounded-xl text-sm font-bold transition-all cursor-pointer"
          style={{
            background: isDarkMode ? '#B8F890' : '#5a9d50',
            color: isDarkMode ? '#0a1608' : '#ffffff',
            border: 'none',
          }}
        >
          Retry Analysis
        </button>
      </Box>
    );
  }

  const {
    hasPracticeHistory,
    companyName,
    contactName,
    practiceCount,
    practiceBaselineScore,
    realMeetingScore,
    overallImprovementPercentage: rawImprovement,
    performanceDelta,
    categoryComparison = [],
    whatImprovedInRealCall = [],
    criticalExecutionGaps = [],
    actionableRoadmapToCloseGaps = [],
    message,
  } = comparison;

  // Score improvement should be the absolute percentage points difference: realMeetingScore - practiceBaselineScore (e.g. 72% - 27% = +45%)
  // Capped strictly between -100% and +100% so it never goes over 100%.
  const computedImprovement =
    typeof realMeetingScore === 'number' && typeof practiceBaselineScore === 'number'
      ? Math.round(realMeetingScore - practiceBaselineScore)
      : rawImprovement;
  const overallImprovementPercentage = Math.min(100, Math.max(-100, Math.round(computedImprovement || 0)));

  const isPositiveGrowth = overallImprovementPercentage >= 0;

  const cleanComp =
    companyName && companyName !== 'Target Company' && companyName.toLowerCase() !== 'it'
      ? companyName
      : '';

  const effectiveCompanyName =
    cleanComp ||
    typedRealData?.company ||
    typedRealData?.customerData?.companyName ||
    typedRealData?.TargetCompanyProfile?.companyName ||
    typedRealData?.meetingNotes?.matchedCompanyName ||
    'Target Company';

  const effectiveContactName =
    contactName ||
    (typedRealData?.companyContact
      ? `${typedRealData.companyContact.firstName || ''} ${typedRealData.companyContact.lastName || ''}`.trim()
      : '') ||
    (typedRealData?.customerContact
      ? typedRealData.customerContact.fullName ||
        `${typedRealData.customerContact.firstName || ''} ${typedRealData.customerContact.lastName || ''}`.trim()
      : '') ||
    typedRealData?.meetingNotes?.matchedContactName ||
    '';

  const trackHex = isDarkMode ? 'rgba(255,255,255,0.08)' : '#e2e8f0';
  const practiceHex = isDarkMode ? 'rgba(255,255,255,0.4)' : '#94a3b8';
  const liveHex = isDarkMode ? '#B8F890' : '#059669';

  return (
    <div className={`space-y-4 pb-8 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
      {/* ---------------------------------------------------- */}
      {/* 1. HEADER — Performance Growth Analysis */}
      {/* ---------------------------------------------------- */}
      <div
        className="rounded-[16px] p-5 space-y-4"
        style={{
          background: colors.surface,
          border: `1px solid ${colors.border}`,
          color: colors.text,
        }}
      >
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <div className="flex items-center gap-2 flex-wrap mb-3">
              <span
                className={`px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider flex items-center gap-1.5 ${
                  isDarkMode
                    ? 'bg-[#B8F890]/10 text-[#B8F890] border border-[#B8F890]/30'
                    : 'bg-emerald-100 text-emerald-800'
                }`}
              >
                <Brain className="w-3.5 h-3.5" />
                Practice vs Reality
              </span>

              <span
                className={`px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1.5 ${
                  isDarkMode
                    ? 'bg-[#252525] text-white/80 border border-[#52515147]'
                    : 'bg-slate-200 text-slate-700'
                }`}
              >
                <Building2 className={`w-3.5 h-3.5 ${isDarkMode ? 'text-[#B8F890]' : 'text-emerald-700'}`} />
                {effectiveCompanyName}
              </span>

              {effectiveContactName && (
                <span
                  className={`px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-1.5 ${
                    isDarkMode
                      ? 'bg-[#252525] text-white/80 border border-[#52515147]'
                      : 'bg-slate-200 text-slate-700'
                  }`}
                >
                  <Users className="w-3.5 h-3.5 opacity-70" />
                  {effectiveContactName}
                </span>
              )}

              {practiceCount > 0 && (
                <span
                  className={`px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1.5 ${
                    isDarkMode
                      ? 'bg-[#252525] text-white/80 border border-[#52515147]'
                      : 'bg-slate-200 text-slate-700'
                  }`}
                >
                  <History className={`w-3.5 h-3.5 ${isDarkMode ? 'text-[#B8F890]' : 'text-emerald-700'}`} />
                  {practiceCount} Practice Session{practiceCount > 1 ? 's' : ''}
                </span>
              )}
            </div>

            <h2 className="text-xl sm:text-2xl font-bold tracking-tight">
              Practice vs Reality Analysis
            </h2>
            <p
              className={`text-md leading-relaxed mt-1 ${
                isDarkMode ? 'text-white/80' : 'text-slate-600 font-medium'
              }`}
            >
              {hasPracticeHistory
                ? `Comparing rep's live customer call with ${effectiveCompanyName} against ${practiceCount} pre-call practice session${
                    practiceCount > 1 ? 's' : ''
                  }.`
                : message || `No pre-call practice sessions found for ${effectiveCompanyName}.`}
            </p>
          </div>

          {hasPracticeHistory && (
            <span
              className={`px-3.5 py-1.5 rounded-full text-sm font-extrabold flex items-center gap-1.5 shrink-0 ${
                isPositiveGrowth
                  ? isDarkMode
                    ? 'bg-[#B8F890]/15 text-[#B8F890] border border-[#B8F890]/30'
                    : 'bg-emerald-100 text-emerald-800'
                  : isDarkMode
                    ? 'bg-[#FF6B6B]/15 text-[#FF6B6B] border border-[#FF6B6B]/30'
                    : 'bg-rose-100 text-rose-800'
              }`}
            >
              {isPositiveGrowth ? '+' : ''}
              {overallImprovementPercentage}% {isPositiveGrowth ? 'Improvement' : 'Drift'}
              {isPositiveGrowth ? (
                <ArrowUpRight className="w-4 h-4" />
              ) : (
                <ArrowDownRight className="w-4 h-4" />
              )}
            </span>
          )}
        </div>

        {hasPracticeHistory ? (
          <div
            className="flex flex-wrap items-center justify-center sm:justify-between gap-x-10 gap-y-4 pt-4 border-t"
            style={{ borderColor: colors.border }}
          >
            <div className="flex items-center gap-8">
              <SemiCircleGauge
                value={practiceBaselineScore}
                trackColor={trackHex}
                fillColor={practiceHex}
                label="Practice Baseline"
                labelColor={isDarkMode ? 'rgba(255,255,255,0.6)' : '#64748b'}
                isDarkMode={isDarkMode}
                size="lg"
              />
              <SemiCircleGauge
                value={realMeetingScore}
                trackColor={trackHex}
                fillColor={liveHex}
                label="Live Meeting"
                labelColor={isDarkMode ? '#B8F890' : '#059669'}
                isDarkMode={isDarkMode}
                size="lg"
              />
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              <span
                className={`text-xs px-2.5 py-1 rounded-lg font-bold ${
                  isPositiveGrowth
                    ? isDarkMode
                      ? 'bg-[#B8F890]/15 text-[#B8F890] border border-[#B8F890]/30'
                      : 'bg-emerald-100 text-emerald-800'
                    : isDarkMode
                      ? 'bg-amber-500/15 text-amber-400 border border-amber-500/30'
                      : 'bg-amber-100 text-amber-800'
                }`}
              >
                {isPositiveGrowth ? 'Improved' : 'Needs Work'}
              </span>
              <span
                className={`text-xs px-2.5 py-1 rounded-lg font-bold ${
                  isDarkMode
                    ? 'bg-[#252525] text-white/80 border border-[#52515147]'
                    : 'bg-slate-200 text-slate-700'
                }`}
              >
                {performanceDelta === 'POSITIVE_GROWTH'
                  ? 'Solid Execution'
                  : performanceDelta === 'STEADY'
                    ? 'Stable Execution'
                    : 'Coaching Attention Needed'}
              </span>
            </div>
          </div>
        ) : (
          <div
            className="pt-4 border-t flex items-center gap-3"
            style={{ borderColor: colors.border }}
          >
            <div
              className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${
                isDarkMode
                  ? 'bg-amber-500/15 text-amber-400 border border-amber-500/30'
                  : 'bg-amber-100 text-amber-800'
              }`}
            >
              <Bot className="w-5 h-5" />
            </div>
            <p className="text-sm font-medium">
              No prior practice session recorded for <strong>{effectiveCompanyName}</strong>. Reps can practice in Digital Twin simulations before real customer calls to measure execution growth.
            </p>
          </div>
        )}
      </div>

      {/* ---------------------------------------------------- */}
      {/* 2. CATEGORY-BY-CATEGORY COMPARISON — Skill Parameters */}
      {/* ---------------------------------------------------- */}
      {categoryComparison.length > 0 && (
        <div
          className="rounded-[16px] p-5 space-y-3"
          style={{
            background: colors.surface,
            border: `1px solid ${colors.border}`,
            color: colors.text,
          }}
        >
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 mb-2">
            <div className="flex items-center gap-2">
              <div
                className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ${
                  isDarkMode ? 'bg-[#B8F890]/10 text-[#B8F890]' : 'bg-emerald-100 text-emerald-800'
                }`}
              >
                <Layers className="w-4 h-4" />
              </div>
              <span className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                Skill Parameter Comparison
              </span>
            </div>
            <div className="flex items-center gap-4 text-xs font-semibold">
              <span className="flex items-center gap-1.5">
                <span
                  className={`w-2.5 h-2.5 rounded-full inline-block ${
                    isDarkMode ? 'bg-white/40' : 'bg-slate-400'
                  }`}
                />{' '}
                Practice
              </span>
              <span className="flex items-center gap-1.5">
                <span
                  className={`w-2.5 h-2.5 rounded-full inline-block ${
                    isDarkMode ? 'bg-[#B8F890]' : 'bg-emerald-600'
                  }`}
                />{' '}
                Live Call
              </span>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
            {categoryComparison.map((cat: CategoryComparisonItem, index: number) => {
              const isImproved = cat.delta > 0;
              const isGap = cat.delta < 0;

              return (
                <div
                  key={index}
                  className="rounded-xl border p-4 space-y-2"
                  style={{
                    borderColor: colors.border,
                    background: isDarkMode ? '#1f1f1f' : '#f8fafc',
                  }}
                >
                  <div className="flex justify-between items-center mb-1">
                    <span
                      className={`font-bold text-base ${isDarkMode ? 'text-white' : 'text-slate-900'}`}
                    >
                      {cat.category}
                    </span>
                    <span
                      className={`text-xs px-2.5 py-0.5 rounded-full font-extrabold flex items-center gap-1 ${
                        isImproved
                          ? isDarkMode
                            ? 'bg-[#B8F890]/15 text-[#B8F890] border border-[#B8F890]/30'
                            : 'bg-emerald-100 text-emerald-800'
                          : isGap
                            ? isDarkMode
                              ? 'bg-[#FF6B6B]/15 text-[#FF6B6B] border border-[#FF6B6B]/30'
                              : 'bg-rose-100 text-rose-800'
                            : isDarkMode
                              ? 'bg-white/10 text-white/80'
                              : 'bg-slate-200 text-slate-700'
                      }`}
                    >
                      {isImproved ? `+${cat.delta}%` : `${cat.delta}%`}
                      {isImproved ? (
                        <ArrowUpRight className="w-3.5 h-3.5" />
                      ) : isGap ? (
                        <ArrowDownRight className="w-3.5 h-3.5" />
                      ) : null}
                    </span>
                  </div>

                  {/* Practice vs Live Gauges */}
                  <div className="flex items-start justify-center gap-2 py-1">
                    <SemiCircleGauge
                      value={cat.practiceAvg}
                      trackColor={trackHex}
                      fillColor={practiceHex}
                      label="Practice Baseline"
                      labelColor={isDarkMode ? 'rgba(255,255,255,0.6)' : '#64748b'}
                      isDarkMode={isDarkMode}
                    />
                    <SemiCircleGauge
                      value={cat.realScore}
                      trackColor={trackHex}
                      fillColor={liveHex}
                      label="Live Meeting"
                      labelColor={isDarkMode ? '#B8F890' : '#059669'}
                      isDarkMode={isDarkMode}
                    />
                  </div>

                  {cat.insight && (
                    <div
                      className="pt-2 text-sm flex items-start gap-2 border-t"
                      style={{
                        borderColor: colors.border,
                        color: isDarkMode ? 'rgba(255,255,255,0.8)' : '#475569',
                      }}
                    >
                      <Lightbulb className={`w-4 h-4 shrink-0 mt-0.5 ${isDarkMode ? 'text-[#B8F890]' : 'text-emerald-600'}`} />
                      <span>{cat.insight}</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ---------------------------------------------------- */}
      {/* 3. WENT WELL & CRITICAL GAPS — Coaching Insights */}
      {/* ---------------------------------------------------- */}
      {(whatImprovedInRealCall.length > 0 || criticalExecutionGaps.length > 0) && (
        <div
          className="rounded-[16px] p-5 space-y-3"
          style={{
            background: colors.surface,
            border: `1px solid ${colors.border}`,
            color: colors.text,
          }}
        >
          <div className="flex items-center gap-2">
            <div
              className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ${
                isDarkMode ? 'bg-[#B8F890]/10 text-[#B8F890]' : 'bg-emerald-100 text-emerald-800'
              }`}
            >
              <Sparkles className="w-4 h-4" />
            </div>
            <span className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
              Coaching Comparison Insights
            </span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
            {/* Left Box: What Improved in Real Call */}
            {whatImprovedInRealCall.length > 0 && (
              <div
                className="rounded-xl border p-4 space-y-2"
                style={{
                  borderColor: isDarkMode ? 'rgba(184,248,144,0.3)' : '#bbf7d0',
                  background: isDarkMode ? '#192318' : '#f0fdf4',
                }}
              >
                <div className="flex items-center gap-2 mb-2">
                  <div
                    className={`w-6 h-6 rounded-lg flex items-center justify-center shrink-0 ${
                      isDarkMode
                        ? 'bg-[#B8F890]/20 text-[#B8F890]'
                        : 'bg-emerald-100 text-emerald-700'
                    }`}
                  >
                    <CheckCircle2 className="w-3.5 h-3.5" />
                  </div>
                  <span
                    className={`text-lg font-bold line-clamp-2 ${
                      isDarkMode ? 'text-[#B8F890]' : 'text-emerald-800'
                    }`}
                  >
                    What Improved in Real Call
                  </span>
                </div>

                <ExpandableBulletList
                  items={whatImprovedInRealCall}
                  previewCount={3}
                  isDarkMode={isDarkMode}
                />
              </div>
            )}

            {/* Right Box: Critical Execution Gaps */}
            {criticalExecutionGaps.length > 0 && (
              <div
                className="rounded-xl border p-4 space-y-2"
                style={{
                  borderColor: isDarkMode ? 'rgba(255,107,107,0.3)' : '#fecdd3',
                  background: isDarkMode ? '#281717' : '#fff1f2',
                }}
              >
                <div className="flex items-center gap-2 mb-2">
                  <div
                    className={`w-6 h-6 rounded-lg flex items-center justify-center shrink-0 ${
                      isDarkMode ? 'bg-[#FF6B6B]/20 text-[#FF6B6B]' : 'bg-rose-100 text-rose-700'
                    }`}
                  >
                    <AlertTriangle className="w-3.5 h-3.5" />
                  </div>
                  <span
                    className={`text-lg font-bold line-clamp-2 ${
                      isDarkMode ? 'text-[#FF6B6B]' : 'text-rose-800'
                    }`}
                  >
                    Critical Execution Gaps
                  </span>
                </div>

                <ExpandableBulletList
                  items={criticalExecutionGaps}
                  previewCount={3}
                  isDarkMode={isDarkMode}
                />
              </div>
            )}
          </div>
        </div>
      )}

      {/* ---------------------------------------------------- */}
      {/* 4. ACTIONABLE ROADMAP — Action Steps */}
      {/* ---------------------------------------------------- */}
      {actionableRoadmapToCloseGaps.length > 0 && (
        <div
          className="rounded-[16px] p-5 space-y-3"
          style={{
            background: colors.surface,
            border: `1px solid ${colors.border}`,
            color: colors.text,
          }}
        >
          <div className="flex items-center gap-2">
            <div
              className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ${
                isDarkMode ? 'bg-[#B8F890]/10 text-[#B8F890]' : 'bg-emerald-100 text-emerald-800'
              }`}
            >
              <Lightbulb className="w-4 h-4" />
            </div>
            <span className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
              Actionable Gap-Closing Roadmap
            </span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
            {actionableRoadmapToCloseGaps.map((step: string, i: number) => {
              const isOpen = expandedRoadmap.has(i);

              let title = `Action Step ${i + 1}`;
              let details = step;

              if (step.includes(':')) {
                const parts = step.split(':');
                title = parts[0].trim();
                details = parts.slice(1).join(':').trim();
              }

              return (
                <div
                  key={i}
                  className="rounded-xl border p-3 space-y-1"
                  style={{
                    borderColor: colors.border,
                    background: isDarkMode ? '#1f1f1f' : '#f8fafc',
                  }}
                >
                  <button
                    onClick={() => toggleRoadmap(i)}
                    className="flex items-center justify-between w-full text-left cursor-pointer"
                    style={{ background: 'transparent', border: 'none', padding: 0 }}
                  >
                    <div className="flex items-center gap-2">
                      <span
                        className={`text-xs font-extrabold px-2 py-0.5 rounded-md ${
                          isDarkMode
                            ? 'bg-[#B8F890]/10 text-[#B8F890] border border-[#B8F890]/20'
                            : 'bg-emerald-100 text-emerald-800'
                        }`}
                      >
                        Step {i + 1}
                      </span>
                      <div
                        className={`text-base font-bold line-clamp-1 ${
                          isDarkMode ? 'text-white' : 'text-slate-900'
                        }`}
                      >
                        {title}
                      </div>
                    </div>
                    <ChevronDown
                      className={`w-4 h-4 shrink-0 transition-transform duration-200 ${
                        isDarkMode ? 'text-white/60' : 'text-slate-500'
                      } ${isOpen ? 'rotate-180' : ''}`}
                    />
                  </button>
                  {isOpen && (
                    <div
                      className={`text-sm pt-1 leading-relaxed ${
                        isDarkMode ? 'text-white/70' : 'text-slate-600 font-medium'
                      }`}
                    >
                      {details}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default PracticeGrowthView;
